import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { useSnackbar } from 'notistack'
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import GetAppIcon from '@mui/icons-material/GetApp'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useSelector } from 'react-redux'

import {
  FormProvider,
  RHFDateRangePickerField,
  RHFSelectField,
} from '../../common/hook-form'
import RHFMultipleSelectField from '../../common/hook-form/RHFMultipleSelectField'
import { apiConstants, post } from '../../common/constants'
import { formatDate } from '../../common/util'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import PageTitle from '../../components/PageTitle'
import _ from 'lodash'

const REPORT_OPTIONS = [
  { label: 'SMS Report', value: 'SMS_REPORT' },
  { label: 'WhatsApp Report', value: 'WA_REPORT' },
]

const flatCardSx = {
  borderRadius: 2.5,
  border: '1px solid #e8e8e8',
  boxShadow: 'none',
}

const SectionHeader = ({ icon, title, count, action }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    {icon && <Box sx={{ display: 'flex', color: '#1565c0' }}>{icon}</Box>}
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a' }}>
      {title}
    </Typography>
    {count != null && (
      <Chip
        label={count}
        size="small"
        sx={{
          height: 18,
          fontSize: '0.68rem',
          fontWeight: 600,
          bgcolor: '#f0f4fa',
          color: '#1565c0',
        }}
      />
    )}
    {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
  </Stack>
)

const Schema = Yup.object().shape({
  reportName: Yup.string().required('Please select a report'),
  merchantRef: Yup.mixed().test(
    'merchantRef-required',
    'Please select at least one merchant',
    (value) => {
      if (typeof value === 'string') return value.trim() !== ''
      if (Array.isArray(value)) return value.length > 0
      return false
    }
  ),
  fromDate: Yup.string().required('From date is required'),
  toDate: Yup.string().required('To date is required'),
})

export default function MonthlySmsWaReport() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: allMerchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const userInfo = useSelector((state) => state?.user?.userInfo)

  const [isGenerating, setIsGenerating] = useState(false)
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(10)
  const [loadingList, setLoadingList] = useState(false)
  const [reload, setReload] = useState(0)
  const [autoRefreshUntil, setAutoRefreshUntil] = useState(0)

  // Same request shape as /finance/reports (Internal.js) — no extra filter.
  useEffect(() => {
    setLoadingList(true)
    const request = {
      "offset": page * sizePerPage,
      "limit": sizePerPage,
      skipCount: true,
      collection: "reports_s3_cache",
      andfilter: [
        {
          field: "createdBy",
          operator: "like",
          value: "@prozo.com"
        },
        {
          field: "reportName",
          operator: "in",
          value: ["SMS_REPORT", "WA_REPORT"],
        },
        {
          field: "isSystemGenerated",
          operator: "eq",
          value: false,
        },
      ],
      sort: {
        field: "createdAt",
        direction: "DESC",
      },
    };
    post(apiConstants.REPORT_GENERIC_SEARCH, request).then((res) => {
      setLoadingList(false)
      res.data.data.map((item) => {
        item.unq = Math.random() * 1000;
        return item;
      });
      setRows(res.data.data || [])
    }).catch((err) => {
      setLoadingList(false)
      console.log(err)
    })
  }, [page, sizePerPage, reload])

  // Background auto-refresh after a generation is queued
  useEffect(() => {
    if (!autoRefreshUntil) return undefined
    const interval = setInterval(() => {
      if (Date.now() > autoRefreshUntil) {
        setAutoRefreshUntil(0)
        return
      }
      setReload((r) => r + 1)
    }, 30000)
    return () => clearInterval(interval)
  }, [autoRefreshUntil])

  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues: {
      reportName: '',
      merchantRef: [],
      fromDate: '',
      toDate: '',
    },
  })
  const { handleSubmit, reset } = methods

  // Same merchant dropdown pattern as src/app/components/UserManagement/AdminUser.js
  const merchantList = Array.isArray(allMerchants)
    ? _.orderBy(allMerchants, "name").map((val) => ({
      value: val.id,
      label: val.name,
    }))
    : []
  const merchantOptions = [{ value: 'ALL', label: 'ALL' }, ...merchantList]

  const toMerchantRef = (value) => {
    if (Array.isArray(value)) {
      if (value.includes('ALL')) return 'ALL'
      return value.join(',')
    }
    return value
  }

  const onSubmit = async (data) => {
    const fromDate = formatDate(data.fromDate, { format: 'yyyy-MM-dd' })
    const toDate = formatDate(data.toDate, { format: 'yyyy-MM-dd' })
    const merchantRef = toMerchantRef(data.merchantRef)

    const payload = {
      reportName: data.reportName,
      fromDate,
      toDate,
      merchantRef,
      overwrite: true,
    }

    setIsGenerating(true)
    try {
      const { data: res } = await post(apiConstants.CREATE_REPORT_ASYNC, payload)
      // If the backend responds with an immediate completion (e.g. cached) — open it
      if (res?.status === 'SUCCESS' && res?.requsetState === 'COMPLETED') {
        const link = res?.reportMeta?.s3Link?.signedLink
        if (link) window.open(link, '_blank')
        enqueueSnackbar('Report ready', { variant: 'success' })
        setPage(0)
        setReload((r) => r + 1)
      } else if (res?.status === 'ERROR') {
        const msg = res?.exception?.message || 'Failed to start report'
        enqueueSnackbar(msg, { variant: 'error' })
      } else {
        // Queued — let the user move on. The list will pick it up.
        enqueueSnackbar(
          'Report generation started. It will appear in the list when ready.',
          { variant: 'info' }
        )
        // Auto-refresh the list every 30s for the next 10 minutes
        setAutoRefreshUntil(Date.now() + 10 * 60 * 1000)
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to start report'
      enqueueSnackbar(msg, { variant: 'error' })
    } finally {
      setIsGenerating(false)
    }
  }

  const onReset = () => {
    if (isGenerating) return
    reset({
      reportName: '',
      merchantRef: [],
      fromDate: '',
      toDate: '',
    })
  }

  // When user changes the page size, reset to page 0 — otherwise they can land
  // on an out-of-range offset (e.g. page=2 size=10 → offset=20; switch to size
  // 100 keeps page=2 → offset=200) which returns no rows.
  const handleSizePerPageChange = (size) => {
    setSizePerPage(size)
    setPage(0)
  }

  // Mirror the column shape used on /finance/reports (AllReports.js + Internal.js)
  const columns = [
    {
      field: 'merchantName',
      headerAlign: 'left',
      hideSortIcons: true,
      align: 'left',
      headerName: 'Merchant Name',
      flex: 1.4,
      renderCell: (params) => {
        const { row } = params
        return <>{row?.merchantName} </>
      },
    },
    {
      field: 'reportName',
      headerAlign: 'left',
      hideSortIcons: true,
      align: 'left',
      headerName: 'Report Name',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {row?.reportName}
              </Grid>
            </Grid>
          </>
        )
      },
    },
    {
      field: 'fromDate',
      headerAlign: 'left',
      hideSortIcons: true,
      align: 'left',
      headerName: 'From Date',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return <>{row?.fromDate ? formatDate(row.fromDate, { format: 'dd MMM, yyyy hh:mm aaa' }) : '-'} </>
      },
    },
    {
      field: 'toDate',
      headerAlign: 'left',
      hideSortIcons: true,
      align: 'left',
      headerName: 'To Date',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return <>{row?.toDate ? formatDate(row.toDate, { format: 'dd MMM, yyyy hh:mm aaa' }) : '-'} </>
      },
    },
    {
      field: 'totalRecords',
      headerAlign: 'left',
      hideSortIcons: true,
      align: 'left',
      headerName: 'Total Records',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return row?.reportAdditionalData?.TOTAL_ORDERS
      },
    },
    {
      field: 'createdBy',
      headerAlign: 'left',
      hideSortIcons: true,
      align: 'left',
      headerName: 'Generated By',
      flex: 1,
    },
    {
      field: 'createdAt',
      headerAlign: 'left',
      hideSortIcons: true,
      align: 'left',
      headerName: 'Generated Date',
      flex: 1,
      renderCell: (params) => {
        const { row } = params
        return <>{row?.createdAt ? formatDate(row.createdAt, { format: 'dd MMM, yyyy hh:mm aaa' }) : '-'} </>
      },
    },
    {
      field: 'action',
      headerAlign: 'left',
      hideSortIcons: true,
      align: 'left',
      headerName: 'Action',
      flex: 0.5,
      renderCell: (params) => {
        const { row } = params
        return (
          <>
            {row?.s3Link?.signedLink && (
              <Tooltip title="Download Report">
                <IconButton
                  onClick={() => window.open(row.s3Link.signedLink, true)}
                  color="primary"
                  size="small"
                >
                  <GetAppIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )
      },
    },
  ]

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      <PageTitle>SMS / WhatsApp Report</PageTitle>

      {/* ─── Page Header ─────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1a1a1a' }}>
            SMS / WhatsApp Report
          </Typography>
          <Typography sx={{ color: '#6b6b6b', fontSize: '0.78rem', mt: 0.3 }}>
            Generate SMS or WhatsApp usage reports for one or more merchants over a chosen date range.
          </Typography>
        </Box>
      </Stack>

      {/* ─── Filters Card ─────────────────────────── */}
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ ...flatCardSx, mb: 2 }}>
          <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
            <SectionHeader
              icon={<FilterAltOutlinedIcon fontSize="small" />}
              title="Report Filters"
            />
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={3}>
                <RHFSelectField
                  name="reportName"
                  label="Report"
                  options={REPORT_OPTIONS}
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <RHFMultipleSelectField
                  name="merchantRef"
                  label="Merchant"
                  outputFormat="array"
                  search
                  options={merchantOptions}
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <RHFDateRangePickerField
                  name="report_range"
                  label="Date Range"
                  maxDate={new Date()}
                  width="100%"
                />
              </Grid>
              <Grid
                item
                xs={12}
                md={3}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <LoadingButton
                  type="submit"
                  variant="contained"
                  size="small"
                  loading={isGenerating}
                >
                  Generate
                </LoadingButton>
                <Button
                  type="button"
                  variant="contained"
                  color="error"
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={onReset}
                  disabled={isGenerating}
                >
                  Reset
                </Button>
              </Grid>
            </Grid>
            <Typography sx={{ mt: 1.5, color: '#9e9e9e', fontSize: '0.7rem' }}>
              Reports are prepared asynchronously. You don't need to stay on this page — once the
              report is ready, it will appear in the list below.
            </Typography>
          </CardContent>
        </Card>
      </FormProvider>

      {/* ─── Generated Reports List ─────────────────────────── */}
      <Card sx={flatCardSx}>
        <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
          <SectionHeader
            icon={<AssessmentOutlinedIcon fontSize="small" />}
            title="Generated Reports"
            action={
              <Tooltip title="Refresh list">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setReload((r) => r + 1)}
                    disabled={loadingList}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            }
          />

          <ProzoDataGrid
            columns={columns}
            autoHeight={true}
            rows={rows}
            sizePerPage={sizePerPage}
            setSizePerPage={handleSizePerPageChange}
            setPage={setPage}
            page={page}
            pagination={true}
            hideFooterPagination={false}
            rowHeight={120}
            rowCount={Number.MAX_VALUE}
            getRowId={(row) => row?.unq}
          />
        </CardContent>
      </Card>
    </Box>
  )
}
